import React, { useCallback, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { CDN_TYPE_SPORT, getCDNUrl } from 'utils';

import {
  BetManager,
  DedicatedEventProvider,
  groupMarketTypeInstructions,
  SiteConfigManager,
  TournamentContext,
  useSportState,
} from '@atlas/core';
import { Flag, Icon } from '@atlas/ui';

import MarketTypeSelector from 'common/MarketTypeSelector/MarketTypeSelector';
import initialTournamentShape from 'common/PropTypes/initialTournamentShape';
import { TournamentOutrights } from 'components/Pages/TournamentOutrights';
import { TournamentScrollGrid } from 'components/Pages/TournamentScrollGrid';
import { TournamentSwipeGrid } from 'components/Pages/TournamentSwipeGrid';
import useSelectedMarketType from 'hooks/useSelectedMarketType';

const onEventClick = payload => {
  if (payload.outcomeId && payload.event) {
    BetManager.selectBet(payload);
  }
};

const FavoriteTournamentHeader = props => {
  const { id, imagePaths, name } = props;
  const imagePath = imagePaths?.FLAG;

  return (
    <div className="tournament-header-info">
      <div className="menu-item-container sport">
        <div className="menu-item-icon">
          <div className="menu-item-icon icon-championship">
            <Flag
              alt={name}
              cdnHostname={getCDNUrl(CDN_TYPE_SPORT)}
              height={16}
              imagePath={imagePath}
              name={id}
              width={20}
            />
          </div>
        </div>
        <div className="menu-item-text">
          <span className="main-label">{name}</span>
        </div>
      </div>
    </div>
  );
};

const FavoriteSport = ({
  amountOfEvents,
  mainMarketsChoice,
  name,
  showMultilines,
  tournaments,
}) => {
  const { id, mainMarketTypes } = useSportState();
  const [isExpanded, setIsExpanded] = useState(true);
  const desktopViewType = SiteConfigManager.getConfig('config.config.sportsbook.desktopViewType');

  const { sportFormMainTypes } = tournaments[0];
  const selectedMarketType = useSelectedMarketType();

  const isAsianView = desktopViewType === 'asian';
  const mainMarketTypesByPeriod = React.useMemo(
    () =>
      groupMarketTypeInstructions(mainMarketTypes, {
        isMultilines: !!(sportFormMainTypes && showMultilines),
        selectedMarketType: !isAsianView && selectedMarketType,
        tournamentMainTypes: sportFormMainTypes,
      }),
    [mainMarketTypes, sportFormMainTypes, selectedMarketType, showMultilines, isAsianView],
  );

  const toggleExpand = useCallback(() => {
    setIsExpanded(isExpanded => !isExpanded);
  }, [setIsExpanded]);

  const iconClassName = cx(!isExpanded && 'arrow-down', isExpanded && 'arrow-up');

  const tournamentsWrapperClassName = cx(!isExpanded && 'display-none');

  const TournamentGridComponent = React.useMemo(() => {
    const mobileGridViewType = SiteConfigManager.getConfig(
      'config.config.sportsbook.mobileGridViewType',
    );

    if (mobileGridViewType === 'swipe') {
      return TournamentSwipeGrid;
    }

    return TournamentScrollGrid;
  }, []);

  return (
    <>
      <div className="menu-item-container sport tournament" onClick={toggleExpand}>
        <div className="menu-item-icon">
          <Icon alt={name} name={`sportIcon-${id}`} type="sports" />
        </div>
        <span className="menu-item-text">
          <span className="main-label">{name}</span>
        </span>
        <div className="menu-item-icon-controls">
          <div className="font-icon-container">
            <span>{amountOfEvents}</span>
            <Icon name={iconClassName} />
          </div>
        </div>
      </div>

      <div className={tournamentsWrapperClassName}>
        {mainMarketsChoice && !isAsianView && <MarketTypeSelector />}
        {tournaments.map(tournament => (
          <TournamentContext.Provider key={tournament.id} value={tournament}>
            <FavoriteTournamentHeader
              id={tournament.id}
              imagePaths={tournament.imagePaths}
              name={tournament.name}
            />
            <TournamentGridComponent
              eventProvider={DedicatedEventProvider}
              hideMarketTypeHeaders
              hideOutcomeTypeHeaders
              marketTypeClassName="col"
              marketTypesPeriodMap={mainMarketTypesByPeriod}
              onEventClick={onEventClick}
            />
            <TournamentOutrights />
          </TournamentContext.Provider>
        ))}
      </div>
    </>
  );
};

FavoriteSport.propTypes = {
  amountOfEvents: PropTypes.number.isRequired,
  mainMarketsChoice: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  showMultilines: PropTypes.bool.isRequired,
  tournaments: PropTypes.arrayOf(initialTournamentShape).isRequired,
};

export default FavoriteSport;
